import { Save } from 'lucide-react';
import { Button } from '@/shared/components';
import type { ApiError } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { problemReason, unanswered } from './catalogProblem';
import type { Issue } from './editorModel';

export interface SaveFailure {
  /** „Nicht gespeichert“ — oder „Keine Antwort“, wenn offen ist, ob der Server übernommen hat. */
  title: string;
  message: string;
  /** Springt zum Feld, das den Grund trägt — fehlt, wenn der Server keinen Ort nennt. */
  onJump?: () => void;
}

/**
 * Der Grund in der Leiste: nach einem Speicherversuch das erste Feld mit Befund, sonst eine
 * Ablehnung des Servers — ein Konflikt hat sein eigenes Banner.
 */
export function saveFailure(
  attempted: boolean,
  issues: Issue[],
  problem: ApiError | null,
  jumpTo: (issue: Issue) => void,
): SaveFailure | null {
  const [first] = issues;
  if (attempted && first) {
    const count = issues.length === 1 ? 'Ein Feld' : `${issues.length} Felder`;
    return {
      title: 'Nicht gespeichert',
      message: `${count} prüfen – ${first.message}`,
      onJump: () => jumpTo(first),
    };
  }
  if (problem && problem.status !== 409) {
    return {
      title: unanswered(problem) ? 'Keine Antwort' : 'Nicht gespeichert',
      message: problemReason(
        problem,
        'Der Server hat die Eingaben abgelehnt. Prüfen Sie Felder, Zeilenumbrüche und Längen.',
      ),
    };
  }
  return null;
}

/**
 * Eine Leiste für die ganze Arbeitsfläche, nur solange etwas offen ist. Sie nennt, was ungespeichert
 * ist, und bleibt bei einer Ablehnung mit dem Grund stehen.
 */
export function EditorSaveBar({
  count,
  summary,
  saving,
  blocked,
  failure,
  onDiscard,
  onSave,
}: {
  count: number;
  summary: string;
  saving: boolean;
  /** Ein Schalter wird gerade gespeichert — beide gehen gegen dieselbe Revision. */
  blocked: boolean;
  failure: SaveFailure | null;
  onDiscard: () => void;
  onSave: () => void;
}) {
  if (count === 0 && !saving && !failure) return null;
  return (
    <div
      role="region"
      aria-label="Speichern"
      className="border-line bg-card/95 shadow-card sticky bottom-3 z-10 flex flex-col gap-3 rounded-lg border p-3 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0 text-sm" aria-live="polite">
        {failure ? (
          <p role="alert" className="text-sealwax flex flex-wrap items-center gap-x-2">
            <b>{failure.title}</b>
            <span className="text-ink">{failure.message}</span>
            {failure.onJump && (
              <button
                type="button"
                onClick={failure.onJump}
                className="text-ink focus-visible:outline-sage min-h-11 px-1 font-semibold underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2"
              >
                Zum Feld
              </button>
            )}
          </p>
        ) : (
          <p className="flex flex-wrap items-center gap-x-2">
            <span
              aria-hidden
              className={cn('size-2 rounded-full', saving ? 'bg-sage' : 'bg-amber')}
            />
            <b className="text-ink">
              {saving
                ? 'Wird gespeichert …'
                : `${count} ungespeicherte ${count === 1 ? 'Änderung' : 'Änderungen'}`}
            </b>
            {!saving && <span className="text-ink-soft truncate">{summary}</span>}
          </p>
        )}
      </div>
      <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex">
        <Button
          type="button"
          variant="ghost"
          className="border-line min-h-11 border"
          disabled={saving}
          onClick={onDiscard}
        >
          Verwerfen
        </Button>
        <Button
          type="button"
          variant="brand"
          className="min-h-11"
          leftIcon={<Save className="size-4" />}
          loading={saving}
          disabled={count === 0 || blocked}
          onClick={onSave}
        >
          Änderungen speichern
        </Button>
      </div>
    </div>
  );
}
